import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../global/storeContext';
import { AuthErrors } from '../components/constants/authErrors';
import { paths } from '../routes';
import { AdminEmails } from '../utils/appConfig';

// TODO remove, this demo shouldn't need to reset the theme.
const defaultTheme = createTheme();

interface IUserCred {
    email: string;
    password: string;
}

export const LoginPage = observer(() => {
    const { userStore, uiStore } = React.useContext(RootStoreContext);
    const [userCred, setUserCred] = React.useState<{ email: string; password: string }>({
        email: '',
        password: '',
    });

    const handleUserCredChange =
        (value: keyof IUserCred) => (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            setUserCred({ ...userCred, [value]: e.currentTarget.value });
        };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const credentials = {
            email: userCred.email ?? '',
            password: userCred.password ?? '',
        };
        uiStore.showLoading();
        await userStore
            .login(credentials.email, credentials.password)
            .then(() => {
                uiStore.hideLoading();
                uiStore.goTo(paths.home);
            })
            .catch((err) => {
                uiStore.hideLoading();
                if (!!err?.code) {
                    const authErr = AuthErrors.find((au) => au.code === err.code);
                    if (!!authErr) {
                        uiStore.error(authErr.message);
                    } else {
                        uiStore.error(
                            'Uh-Oh! We were unable to sign into your account. Try again later',
                        );
                    }

                    if (authErr?.code === 'email-not-verified' && !!err.user) {
                        uiStore.error('Unauthorized user');
                    }
                }
            });
    };

    const isAdminUser = !!AdminEmails.find((user) => user === userCred.email);

    return (
        <ThemeProvider theme={defaultTheme}>
            <Container component='main' maxWidth='xs'>
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
                        <LockOutlinedIcon />
                    </Avatar>
                    <Typography variant='h4'>Realhaus Admin</Typography>
                    <Typography component='h1' variant='h5'>
                        Sign in
                    </Typography>
                    <Box component='form' onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        <TextField
                            id='email'
                            label='Email'
                            placeholder='your@email.com'
                            type='email'
                            fullWidth
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={userCred.email}
                            onChange={handleUserCredChange('email')}
                            required
                        />

                        <TextField
                            fullWidth
                            id='password'
                            label='Password'
                            placeholder='Password'
                            type='password'
                            margin='dense'
                            InputLabelProps={{
                                shrink: true,
                            }}
                            value={userCred.password}
                            onChange={handleUserCredChange('password')}
                            required
                        />
                        <Button
                            type='submit'
                            fullWidth
                            variant='contained'
                            sx={{ mt: 3, mb: 2 }}
                            disabled={!isAdminUser}
                        >
                            log In
                        </Button>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
});
